/**
 * numberToWords.ts — UC-009 telephonic CZK amount disclosure pro Petra,
 * který diktuje Josefovi částku po telefonu (REQ-BIZ-040).
 *
 * Czech grammar — pluralizace "koruna" závisí na poslední číslici:
 *
 *   - 0          → "0 korun českých"
 *   - 1          → "1 koruna česká"
 *   - 2, 3, 4    → "2 koruny české"
 *   - 5, 6, …, 0 → "5 korun českých"
 *
 * Edge cases:
 *   - 11..14, 111..114 ... → "korun" (plural mass, ne "koruny", per Czech grammar)
 *   - 21, 31, 41 ...       → "koruna česká" (jednotné číslo se poslední číslicí 1)
 *
 * Haléře (haléř / haléře / haléřů) následují stejný pattern jen jiné slovo.
 *
 * Range: 0 ... 999_999 (REQ-BIZ-041 acceptable upper bound — měsíční rozpočet
 * nedosahuje milionu, jen pojistka proti přetečení).
 *
 * Use:
 *
 *   formatCzkInWords(1234)        → "1234 Kč (jedna koruna 234 korun českých)"
 *   formatCzkInWords(2345.5)      → "2345,50 Kč (slovy: 2 koruny české 345 a 50 haléřů)"
 *   amountInWords(1)              → "jedna koruna česká"
 *   amountInWords(5)              → "5 korun českých"
 *   amountInWords(0)              → "0 korun českých"
 */

/**
 * Pravidlo pluralizace "koruna" — vrátí variantu podle hodnoty (positive integer).
 *
 * 0    → "korun"
 * 1    → "koruna"
 * 2-4  → "koruny"
 * 5+   → "korun"
 *
 * Pravidlo zohledňuje:
 *   - 11..14, 21..24, ... 91..94: pro 11..14 plat "korun" (mass plural), pro 21..24
 *     platí "koruny" (per Czech grammar — single ones digit drives form mimo teens).
 */
export function korunaForm(amount: number): "koruna" | "koruny" | "korun" {
  const n = Math.floor(Math.abs(amount));
  const lastTwo = n % 100;
  // 11..14: korun (teens exception — sometimes 11..19 all use mass plural,
  // ale per ÚJČ ČSAV: 11–19 → "korun" jako mass plural)
  if (lastTwo >= 11 && lastTwo <= 14) return "korun";
  const lastDigit = n % 10;
  if (lastDigit === 1) return "koruna";
  if (lastDigit >= 2 && lastDigit <= 4) return "koruny";
  return "korun";
}

/**
 * Adjective form pro "česká" — agreement s "koruna" gender + case:
 *   1   → "česká"  (singular nom.)
 *   2-4 → "české" (plural nom.)
 *   5+  → "českých" (plural gen.)
 */
export function ceskaAdjective(amount: number): "česká" | "české" | "českých" {
  const n = Math.floor(Math.abs(amount));
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return "českých";
  const lastDigit = n % 10;
  if (lastDigit === 1) return "česká";
  if (lastDigit >= 2 && lastDigit <= 4) return "české";
  return "českých";
}

/**
 * Pravidlo pluralizace "haléř" — analogická logika ke koruně.
 *   1    → "haléř"
 *   2-4  → "haléře"
 *   5+   → "haléřů"
 *   0    → "haléřů"
 *   11..14 → "haléřů"
 */
export function halerForm(amount: number): "haléř" | "haléře" | "haléřů" {
  const n = Math.floor(Math.abs(amount));
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return "haléřů";
  const lastDigit = n % 10;
  if (lastDigit === 1) return "haléř";
  if (lastDigit >= 2 && lastDigit <= 4) return "haléře";
  return "haléřů";
}

/**
 * Format whole-CZK amount slovně. Pro telephonic mode používá kombinaci
 * číslice + správné slovo (Petr diktuje "tři sta padesát sedm korun českých").
 *
 * Acceptable range: 0..999_999 (over → throws). Negative → throws.
 */
export function amountInWords(amount: number): string {
  if (!Number.isFinite(amount)) {
    throw new RangeError(`amountInWords: not a finite number (received ${amount})`);
  }
  if (amount < 0) {
    throw new RangeError(`amountInWords: negative values not supported (received ${amount})`);
  }
  if (amount > 999_999) {
    throw new RangeError(`amountInWords: over million not supported (received ${amount})`);
  }
  const whole = Math.floor(amount);
  const haleru = Math.round((amount - whole) * 100);
  const wholeWord = `${whole} ${korunaForm(whole)} ${ceskaAdjective(whole)}`;
  if (haleru === 0) {
    return wholeWord;
  }
  return `${wholeWord} a ${haleru} ${halerForm(haleru)}`;
}

/**
 * Format "1234 Kč (slovy: …)" — display string pro PushAssistDetailCard
 * Disclosure component. Pro PSU jednoduchá kombinace numeric + slovní popis.
 */
export function formatCzkWithDisclosure(amount: number): {
  numeric: string;
  inWords: string;
} {
  const numeric = amount % 1 === 0
    ? `${amount} Kč`
    : `${amount.toFixed(2).replace(".", ",")} Kč`;
  return {
    numeric,
    inWords: amountInWords(amount),
  };
}
